// §34.10 (M63.2) — the shell's picker overlay: dim the desktop, take a rect, get out of the way.
//
// It lives in `host/` and is built imperatively rather than as a React component, and both are
// deliberate:
//
//   • **`host/`, because it is shell furniture.** §34.10 says "shell dims and the picker overlay
//     draws", and an overlay drawn by the *app* could not cover the desktop — an app lives inside its
//     window (§5.7's `.win-host`) and has no way out of it. Nor should it: a surface that dims every
//     window and reads a rect over all of them is precisely the sort of reach D-059 keeps granted.
//   • **Imperative, because it is a modal question with an answer.** `pick()` is one `Promise` that
//     resolves to a rect or to `null`, and every listener it added is gone by the time it settles.
//     A React overlay would need state in a provider, a subscription in the tree and a way to hand a
//     value back out — three moving parts for a function that returns a rectangle.
//
// **It removes itself before the capture happens**, which is not an optimisation: the dim and the
// selection box are real DOM in the shell document, so `getDisplayMedia` would photograph them. The
// order §34.10 states — picker, *then* the browser's prompt, then the frame — is the order that makes
// the snip a picture of the desktop rather than a picture of Snip asking about the desktop.
//
// Everything is drawn in code (D-043) with §6.2 tokens, so it matches whichever skin is mounted.
import type { CssRect, SnipMode, SnipPick, Win } from '@hangar/shared';
import { dragRect, isUsableRegion } from '@hangar/shared';

export interface SnipOverlayDeps {
  /** The windows the *window* mode may pick, topmost first. Minimized ones are already excluded. */
  windows(): readonly Win[];
  /** The hint each mode shows, from `COPY.snip` — passed in so this module holds no strings. */
  hintFor(mode: SnipMode): string;
  /** Where the overlay is attached. Defaults to `document.body`. */
  root?: HTMLElement;
}

/** Class names, so the CSS and the test agree on one spelling. */
export const SNIP_OVERLAY_CLASS = 'snip-overlay';
export const SNIP_SELECTION_CLASS = 'snip-overlay-selection';

/**
 * Dim the desktop, let the user choose, and answer.
 *
 * Resolves to a `SnipPick` — whose `rect` is `null` for the full desktop, which needs no crop — or to
 * `null` for a cancel, which is `Esc`, a right-click, or a drag too small to have been meant
 * (`isUsableRegion`). §34.17 makes a cancel produce no capture and no partial file, so answering
 * `null` here is the whole of that behaviour: nothing downstream runs.
 */
export function pickSnipRegion(mode: SnipMode, deps: SnipOverlayDeps): Promise<SnipPick | null> {
  const root = deps.root ?? document.body;

  const overlay = document.createElement('div');
  overlay.className = SNIP_OVERLAY_CLASS;
  overlay.dataset['mode'] = mode;
  overlay.dataset['testid'] = 'snip-overlay';
  overlay.setAttribute('data-testid', 'snip-overlay');
  // Focusable, so `Esc` reaches it without depending on what had focus when Snip was clicked.
  overlay.tabIndex = -1;

  const hint = document.createElement('p');
  hint.className = 'snip-overlay-hint';
  hint.setAttribute('data-testid', 'snip-overlay-hint');
  hint.textContent = deps.hintFor(mode);
  overlay.append(hint);

  const selection = document.createElement('div');
  selection.className = SNIP_SELECTION_CLASS;
  selection.setAttribute('data-testid', 'snip-overlay-selection');
  selection.hidden = true;
  overlay.append(selection);

  root.append(overlay);
  overlay.focus();

  return new Promise<SnipPick | null>((resolve) => {
    let start: { x: number; y: number } | null = null;
    let settled = false;

    const paint = (rect: CssRect | null): void => {
      if (rect === null) {
        selection.hidden = true;
        return;
      }
      selection.hidden = false;
      selection.style.left = `${rect.x}px`;
      selection.style.top = `${rect.y}px`;
      selection.style.width = `${rect.width}px`;
      selection.style.height = `${rect.height}px`;
    };

    /** Tear down first, resolve second — so nothing the caller does next sees a live overlay. */
    const settle = (answer: SnipPick | null): void => {
      if (settled) return;
      settled = true;
      overlay.removeEventListener('pointerdown', onPointerDown);
      overlay.removeEventListener('pointermove', onPointerMove);
      overlay.removeEventListener('pointerup', onPointerUp);
      overlay.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('keydown', onKeyDown, true);
      overlay.remove();
      resolve(answer);
    };

    /** The topmost window whose rect contains a point (§5.3 — `z` is the stacking truth). */
    const windowAt = (x: number, y: number): Win | null => {
      for (const win of deps.windows()) {
        const { rect } = win;
        if (x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h) return win;
      }
      return null;
    };

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      // Captured and stopped: `Esc` while the picker is up is the picker's, not the focused window's
      // (§9.7's arbitration says the shell's own surface wins while it is showing).
      event.preventDefault();
      event.stopPropagation();
      settle(null);
    }

    function onContextMenu(event: MouseEvent): void {
      // A right-click is the other universal "no" — and without this the skin's own context menu
      // would open *behind* the overlay, which is worse than either outcome.
      event.preventDefault();
      settle(null);
    }

    function onPointerDown(event: PointerEvent): void {
      if (event.button !== 0) return;
      if (mode === 'full') {
        // Nothing to choose: the whole frame is the answer, and `null` says "no crop".
        settle({ rect: null });
        return;
      }
      if (mode === 'window') {
        const win = windowAt(event.clientX, event.clientY);
        // A click on bare desktop in window mode is not a cancel and not a capture — there is no
        // window there, so the picker keeps waiting rather than silently snipping the wallpaper.
        if (win === null) return;
        settle({
          rect: { x: win.rect.x, y: win.rect.y, width: win.rect.w, height: win.rect.h },
          windowId: win.id,
        });
        return;
      }
      start = { x: event.clientX, y: event.clientY };
      overlay.setPointerCapture(event.pointerId);
      paint(dragRect(start, start));
    }

    function onPointerMove(event: PointerEvent): void {
      if (mode === 'window') {
        // Preview the window under the pointer, so the user is never choosing blind — §34.10's
        // requirement that coordinates come from the overlay *before* the capture.
        const win = windowAt(event.clientX, event.clientY);
        paint(win === null ? null : { x: win.rect.x, y: win.rect.y, width: win.rect.w, height: win.rect.h });
        return;
      }
      if (start === null) return;
      paint(dragRect(start, { x: event.clientX, y: event.clientY }));
    }

    function onPointerUp(event: PointerEvent): void {
      if (mode !== 'region' || start === null) return;
      const rect = dragRect(start, { x: event.clientX, y: event.clientY });
      start = null;
      // A click rather than a drag is a cancel. Treating it as an 8-pixel snip would be obeying the
      // input rather than the intent, and there is no way to undo a file that got written.
      settle(isUsableRegion(rect) ? { rect } : null);
    }

    overlay.addEventListener('pointerdown', onPointerDown);
    overlay.addEventListener('pointermove', onPointerMove);
    overlay.addEventListener('pointerup', onPointerUp);
    overlay.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown, true);
  });
}
